import { TK } from '../store/translations/translationKeys';
import { ClientPropertySettings } from '../models/api-models';

export interface PlaceholderItem {
    key: string;
    label: string;
    section?: string;
}

export const getPlaceholderItems = (
    t: (key: TK) => string,
    clientProperties?: Record<string, ClientPropertySettings>,
): PlaceholderItem[] => {
    const s = {
        client:       t(TK.client),
        company:      t(TK.company),
        appointment:  t(TK.appointment),
        professional: t(TK.professional),
        links:        t(TK.links),
        invoice:      t(TK.invoice),
    };

    const items: PlaceholderItem[] = [
        // Client
        { key: 'client-name',                      label: t(TK.name),                 section: s.client },
        { key: 'client-identification-number',     label: t(TK.identificationNumber), section: s.client },
        { key: 'client-tax-number',                label: t(TK.taxNumber),            section: s.client },
        { key: 'client-health-number',             label: t(TK.healthNumber),         section: s.client },
        { key: 'client-subsystem-name',            label: t(TK.subsystemName),        section: s.client },
        { key: 'client-subsystem-number',          label: t(TK.subsystemNumber),      section: s.client },
        { key: 'client-birthday',                  label: t(TK.birthday),             section: s.client },
        { key: 'client-age',                       label: t(TK.age),                  section: s.client },
        { key: 'client-phone-number',              label: t(TK.phoneNumber),          section: s.client },
        { key: 'client-email',                     label: t(TK.email),                section: s.client },
        { key: 'client-file-number',               label: t(TK.clientFileNumber),     section: s.client },
        // Company
        { key: 'company-name',                     label: t(TK.companyName),          section: s.company },
        // Appointment
        { key: 'date',                             label: t(TK.date),                 section: s.appointment },
        { key: 'date-day',                         label: t(TK.day),                  section: s.appointment },
        { key: 'date-month',                       label: t(TK.month),                section: s.appointment },
        { key: 'date-year',                        label: t(TK.year),                 section: s.appointment },
        { key: 'week-day',                         label: t(TK.weekDay),              section: s.appointment },
        { key: 'time',                             label: t(TK.time),                 section: s.appointment },
        { key: 'appointment-type',                 label: t(TK.appointmentType),      section: s.appointment },
        { key: 'appointment-price',                label: t(TK.price),                section: s.appointment },
        { key: 'appointment-start',                label: t(TK.startTime),            section: s.appointment },
        { key: 'appointment-end',                  label: t(TK.endTime),              section: s.appointment },
        { key: 'appointment-room',                 label: t(TK.room),                 section: s.appointment },
        // Professional
        { key: 'professional-name',                label: t(TK.name),                 section: s.professional },
        { key: 'professional-registration-number', label: t(TK.registrationNumber),  section: s.professional },
        { key: 'professional-email',               label: t(TK.email),                section: s.professional },
        { key: 'professional-phone-number',        label: t(TK.phoneNumber),          section: s.professional },
        // Invoice-specific
        { key: 'price',                            label: t(TK.price),                section: s.invoice },
        { key: 'invoice-appointment-type',         label: t(TK.appointmentType),      section: s.invoice },
    ];

    if (clientProperties) {
        for (const [key, prop] of Object.entries(clientProperties)) {
            items.push({ key: `prop:${key}`, label: key, section: s.client });
        }
    }

    return items;
};

export interface PlaceholderContext {
    // Client
    clientName?: string
    clientIdentificationNumber?: string
    clientTaxNumber?: string
    clientHealthNumber?: string
    clientSubsystemName?: string
    clientSubsystemNumber?: string
    clientBirthday?: string
    clientPhoneNumber?: string
    clientEmail?: string
    clientFileNumber?: string
    // Company
    companyName?: string
    // Appointment (communication format: formatted date/time strings)
    date?: string
    day?: string
    weekDay?: string
    time?: string
    appointmentType?: string
    professionalName?: string
    appointmentPrice?: string
    // Appointment (report format: raw field values)
    appointmentDate?: string
    appointmentStart?: string
    appointmentEnd?: string
    appointmentRoom?: string
    // Professional (extended)
    professionalRegistrationNumber?: string
    professionalEmail?: string
    professionalPhoneNumber?: string
    // Follow-up
    firstAppointmentDate?: string
    firstAppointmentTime?: string
    // Links
    paymentLink?: string
    bookingLink?: string
    unsubscribeLink?: string
    reviewUrl?: string
    surveyLink?: string
    consentLink?: string
    // Misc
    customFarewell?: string
    // Invoice-specific
    price?: string
    invoiceAppointmentType?: string
    // Dynamic client properties: {prop:KEY}
    clientProperties?: Record<string, string>
}

const PT_MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']

const computeDateComponents = (rawDate: string): { day: string; month: string; year: string } | null => {
    const m = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (!m) return null
    const year = Number(m[1]), month = Number(m[2]) - 1, day = Number(m[3])
    if (month < 0 || month > 11 || day < 1 || day > 31) return null
    return { day: String(day), month: PT_MONTHS[month], year: String(year) }
}

const computeAge = (birthday: string): string => {
    const match = birthday.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (!match) return ''
    const year = Number(match[1]), month = Number(match[2]), day = Number(match[3])
    const today = new Date()
    let age = today.getFullYear() - year
    if (today.getMonth() + 1 < month || (today.getMonth() + 1 === month && today.getDate() < day)) age--
    return age >= 0 ? String(age) : ''
}

export const resolvePlaceholders = (template: string, ctx: PlaceholderContext): string => {
    let result = template
        // Client
        .replaceAll('{client-name}', ctx.clientName ?? '')
        .replaceAll('{client-identification-number}', ctx.clientIdentificationNumber ?? '')
        .replaceAll('{client-tax-number}', ctx.clientTaxNumber ?? '')
        .replaceAll('{client-health-number}', ctx.clientHealthNumber ?? '')
        .replaceAll('{client-subsystem-name}', ctx.clientSubsystemName ?? '')
        .replaceAll('{client-subsystem-number}', ctx.clientSubsystemNumber ?? '')
        .replaceAll('{client-birthday}', ctx.clientBirthday ?? '')
        .replaceAll('{client-phone-number}', ctx.clientPhoneNumber ?? '')
        .replaceAll('{client-email}', ctx.clientEmail ?? '')
        .replaceAll('{client-file-number}', ctx.clientFileNumber ?? '')
        .replaceAll('{client-age}', ctx.clientBirthday ? computeAge(ctx.clientBirthday) : '')
        // Company
        .replaceAll('{company-name}', ctx.companyName ?? '')
        .replaceAll('{companyName}', ctx.companyName ?? '') // Legacy camelCase alias
        // Appointment
        .replaceAll('{date}', ctx.date ?? '')
        .replaceAll('{day}', ctx.day ?? '')
        .replaceAll('{week-day}', ctx.weekDay ?? '')
        .replaceAll('{time}', ctx.time ?? '')
        .replaceAll('{appointment-type}', ctx.appointmentType ?? '')
        .replaceAll('{professional-name}', ctx.professionalName ?? '')
        .replaceAll('{appointment-price}', ctx.appointmentPrice ?? '')
        // Follow-up
        .replaceAll('{first-appointment-date}', ctx.firstAppointmentDate ?? '')
        .replaceAll('{first-appointment-time}', ctx.firstAppointmentTime ?? '')
        // Links
        .replaceAll('{payment-link}', ctx.paymentLink ?? '')
        .replaceAll('{booking-link}', ctx.bookingLink ?? '')
        .replaceAll('{unsubscribe-link}', ctx.unsubscribeLink ?? '')
        .replaceAll('{review-url}', ctx.reviewUrl ?? '')
        .replaceAll('{survey-link}', ctx.surveyLink ?? '')
        .replaceAll('{consent-link}', ctx.consentLink ?? '')
        // Misc
        .replaceAll('{custom-farewell}', ctx.customFarewell ?? '')
        // Appointment (report format)
        .replaceAll('{appointment-date}', ctx.appointmentDate ?? '')
        .replaceAll('{appointment-start}', ctx.appointmentStart ?? '')
        .replaceAll('{appointment-end}', ctx.appointmentEnd ?? '')
        .replaceAll('{appointment-room}', ctx.appointmentRoom ?? '')
        // Professional (extended)
        .replaceAll('{professional-registration-number}', ctx.professionalRegistrationNumber ?? '')
        .replaceAll('{professional-email}', ctx.professionalEmail ?? '')
        .replaceAll('{professional-phone-number}', ctx.professionalPhoneNumber ?? '')
        // Invoice-specific
        .replaceAll('{price}', ctx.price ?? '')
        .replaceAll('{invoice-appointment-type}', ctx.invoiceAppointmentType ?? '')
        .replaceAll('{professional}', ctx.professionalName ?? '') // Legacy alias for professional-name

    // {date-day}, {date-month}, {date-year} — derived from appointmentDate at resolution time
    const dateComponents = ctx.appointmentDate ? computeDateComponents(ctx.appointmentDate) : null
    result = result
        .replaceAll('{date-day}', dateComponents?.day ?? '')
        .replaceAll('{date-month}', dateComponents?.month ?? '')
        .replaceAll('{date-year}', dateComponents?.year ?? '')

    if (ctx.clientProperties)
        for (const [key, value] of Object.entries(ctx.clientProperties))
            result = result.replaceAll(`{prop:${key}}`, value ?? '')

    return result
}
